import React, { useState } from 'react';
import { AgentVersion, ModelsConfig } from '@/redux/internal-services/agentsApi';
import { AgentFormValues, Modal, relTime, promptText, lineDiff, labelCls, inputCls } from './agentShared';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import { cn } from '@/utils/cn';

// ── VersionsTab ────────────────────────────────────────────────────────────────

interface VersionsTabProps {
  versions: AgentVersion[];
  publishedId?: string;
  dirty: boolean;
  latestN?: number;
  onSaveVersion: () => void;
  onView: (v: AgentVersion) => void;
}

export const VersionsTab: React.FC<VersionsTabProps> = ({
  versions,
  publishedId,
  dirty,
  latestN,
  onSaveVersion,
  onView,
}) => (
  <div>
    <div className="mb-3 flex items-center justify-between">
      <p className="text-sm text-ui-text2">
        Each version is a snapshot of the prompt, model, parameters, and tools. Publish one to mark it live.
      </p>
      <Button type="button" size="sm" onClick={onSaveVersion}>
        Save version
      </Button>
    </div>

    <div
      className={cn(
        'mb-5 flex items-center gap-3 rounded-card border p-4',
        dirty ? 'border-warning/40 bg-warning/10' : 'border-ui-border bg-ui-card',
      )}
    >
      <div className="flex-1">
        <div className="text-sm font-medium text-ui-text">Working draft</div>
        <div className="text-xs text-ui-text2">
          {dirty
            ? latestN
              ? `Unsaved changes since v${latestN}`
              : 'Not yet versioned'
            : `Matches v${latestN}`}
        </div>
      </div>
      {dirty && (
        <Button type="button" variant="outline" size="sm" onClick={onSaveVersion}>
          Save version
        </Button>
      )}
    </div>

    {versions.length === 0 && <EmptyState variant="inline" title="No versions saved yet." />}

    <div className="space-y-2">
      {versions.map((v) => (
        <div key={v.id} className="card-surface flex items-center gap-3 px-4 py-3">
          <span className="rounded-chip bg-ui-subtle px-2 py-0.5 font-mono text-xs font-medium text-ui-text">
            v{v.n}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-ui-text">{v.message}</div>
            <div className="text-xs text-ui-text3">{relTime(v.created_at)}</div>
          </div>
          {v.id === publishedId && <Badge variant="success">● Live</Badge>}
          <Button type="button" variant="outline" size="sm" onClick={() => onView(v)}>
            View
          </Button>
        </div>
      ))}
    </div>
  </div>
);

// ── SaveVersionModal ───────────────────────────────────────────────────────────

interface SaveVersionModalProps {
  nextN: number;
  onClose: () => void;
  onSave: (message: string, publish: boolean) => void;
}

export const SaveVersionModal: React.FC<SaveVersionModalProps> = ({ nextN, onClose, onSave }) => {
  const [message, setMessage] = useState('');
  const [publish, setPublish] = useState(true);
  return (
    <Modal
      title={`Save version ${nextN}`}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => onSave(message.trim() || `Version ${nextN}`, publish)}>
            Save version
          </Button>
        </>
      }
    >
      <div className="mb-4">
        <label className={labelCls}>
          What changed? <span className="text-ui-text3 font-normal">— shows in history</span>
        </label>
        <input
          autoFocus
          className={inputCls}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Tightened refund policy, added lookup tool"
        />
      </div>
      <label className="flex cursor-pointer items-center gap-2.5 rounded-control border border-ui-border px-3 py-2.5 text-sm">
        <input
          type="checkbox"
          checked={publish}
          onChange={(e) => setPublish(e.target.checked)}
          className="h-4 w-4 accent-accent"
        />
        Publish this version (mark it live)
      </label>
      <p className="mt-2 text-xs text-ui-text2">Snapshots the current draft and saves the agent.</p>
    </Modal>
  );
};

// ── ViewVersionModal ───────────────────────────────────────────────────────────

interface ViewVersionModalProps {
  version: AgentVersion;
  current: AgentFormValues;
  isPublished: boolean;
  onClose: () => void;
  onRestore: () => void;
  onPublish: () => void;
}

export const ViewVersionModal: React.FC<ViewVersionModalProps> = ({
  version,
  current,
  isPublished,
  onClose,
  onRestore,
  onPublish,
}) => {
  const snap = version.snapshot as Partial<AgentFormValues>;
  const diff = lineDiff(
    promptText(snap.system_prompts as Array<{ title?: string; prompt: string }>),
    promptText(current.system_prompts),
  );
  const changed = diff.some((l) => l.t !== 'same');

  return (
    <Modal
      wide
      title={`Version ${version.n}`}
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Close
          </Button>
          {!isPublished && (
            <Button type="button" variant="outline" size="sm" onClick={onPublish}>
              Publish to live
            </Button>
          )}
          <Button type="button" size="sm" onClick={onRestore}>
            Restore to draft
          </Button>
        </>
      }
    >
      <div className="mb-3 flex items-center gap-2 text-sm">
        <span className="rounded-chip bg-ui-subtle px-2 py-0.5 font-mono text-xs">v{version.n}</span>
        <span className="text-ui-text">{version.message}</span>
        {isPublished && <Badge variant="success">● Live</Badge>}
        <span className="flex-1" />
        <span className="text-xs text-ui-text3">{relTime(version.created_at)}</span>
      </div>

      <div className="eyebrow mb-1">Model</div>
      <p className="mb-3 text-sm font-mono">
        {String((snap.models as ModelsConfig)?.text)}
        {(snap.models as ModelsConfig)?.text !== (current.models as ModelsConfig)?.text && (
          <span className="text-success-text"> → {(current.models as ModelsConfig)?.text} (draft)</span>
        )}
      </p>

      <div className="eyebrow mb-1">
        Prompt — this version → current draft
      </div>
      {changed ? (
        <div className="max-h-72 overflow-y-auto rounded-chip border border-ui-border bg-ui-subtle p-2 font-mono text-xs leading-relaxed">
          {diff.map((l, i) => (
            <div
              key={i}
              className={
                l.t === 'add'
                  ? 'bg-success/10 text-success-text'
                  : l.t === 'del'
                  ? 'bg-danger/10 text-danger-text line-through'
                  : 'text-ui-text2'
              }
            >
              <span className="select-none pr-1 text-ui-text3">
                {l.t === 'add' ? '+' : l.t === 'del' ? '−' : ' '}
              </span>
              {l.v || ' '}
            </div>
          ))}
        </div>
      ) : (
        <p className="rounded-control border border-ui-border bg-ui-card p-3 text-sm text-ui-text3">
          Prompt matches the current draft.
        </p>
      )}
    </Modal>
  );
};
